<template>
  <v-container
    class="d-flex"
    fluid
    style="min-height: 100%;"
  >
    <v-row>
      <v-col>
        <v-container fluid>
          <v-row>
            <v-col cols="12">
              <v-autocomplete
                ref="docketEntryTypeOptions"
                v-model="docketEntry.docket_entry_type_id"
                :items="docketEntryTypeOptions"
                :label="actionTypeLabel"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.docket_entry_type_id)"
                @input="_ => v$.docketEntry.docket_entry_type_id.$touch()"
                @blur="_ => v$.docketEntry.docket_entry_type_id.$touch()"
              >
                <template #selection="{ item }">
                  <span>
                    {{ item.text }}
                    <v-icon
                      v-if="item.has_workflows"
                      class="ml-1"
                      color="black"
                      size="18"
                    >
                      mdi-arrow-decision-outline
                    </v-icon>
                    <v-icon
                      v-if="item.has_checklist"
                      class="ml-1"
                      color="black"
                      size="18"
                    >
                      mdi-format-list-checks
                    </v-icon>
                    <v-icon
                      v-if="item.has_automated_text"
                      class="ml-1"
                      color="black"
                      size="18"
                    >
                      mdi-message-flash-outline
                    </v-icon>
                    <v-icon
                      v-if="item.has_notifications"
                      class="ml-1"
                      color="black"
                      size="18"
                    >
                      mdi-message-outline
                    </v-icon>
                  </span>
                </template>
                <template #item="{ item }">
                  <v-list-item-content>
                    <v-list-item-title>
                      {{ item.text }}
                      <v-icon
                        v-if="item.has_workflows"
                        class="ml-1"
                        color="black"
                        size="18"
                      >
                        mdi-arrow-decision-outline
                      </v-icon>
                      <v-icon
                        v-if="item.has_checklist"
                        class="ml-1"
                        color="black"
                        size="18"
                      >
                        mdi-format-list-checks
                      </v-icon>
                      <v-icon
                        v-if="item.has_automated_text"
                        class="ml-1"
                        color="black"
                        size="18"
                      >
                        mdi-message-flash-outline
                      </v-icon>
                      <v-icon
                        v-if="item.has_notifications"
                        class="ml-1"
                        color="black"
                        size="18"
                      >
                        mdi-message-outline
                      </v-icon>
                    </v-list-item-title>
                  </v-list-item-content>
                </template>
              </v-autocomplete>
            </v-col>

            <v-col cols="12">
              <UserSelect
                ref="assignee"
                v-model="docketEntry.assignee_id"
                :allowed-users="allowedUsers"
                deletable-chips
                hide-disabled
                hide-selected
                label="Assignee *"
                no-data-text="No users available"
                only-assignable
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.assignee_id)"
                @input="_ => v$.docketEntry.assignee_id.$touch()"
                @blur="_ => v$.docketEntry.assignee_id.$touch()"
              />
            </v-col>

            <v-col
              cols="12"
            >
              <v-select
                v-model="docketEntry.attendee_id"
                :items="attendees"
                label="Attendee *"
                no-data-text="No Parties available"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.attendee_id)"
                @input="_ => v$.docketEntry.attendee_id.$touch()"
                @blur="_ => v$.docketEntry.attendee_id.$touch()"
              />
            </v-col>

            <v-col cols="12">
              <v-radio-group
                v-model="docketEntry.contact_type"
              >
                <v-radio
                  :disabled="!attendeeHasEmail"
                  :label="emailLabel"
                  value="email"
                />
                <v-radio
                  :disabled="!attendeeHasPhone"
                  :label="phoneLabel"
                  value="phone"
                />
              </v-radio-group>
            </v-col>

            <v-col
              cols="12"
              class="d-flex"
            >
              <v-menu
                v-if="hasAppointmentLocations"
                bottom
                right
                offset-y
              >
                <template #activator="{ on, attrs }">
                  <v-btn
                    class="me-4"
                    depressed
                    style="min-height: 56px;"
                    v-bind="attrs"
                    v-on="on"
                  >
                    <v-icon>mdi-map-marker-outline</v-icon>
                    <v-icon>mdi-menu-down</v-icon>
                  </v-btn>
                </template>

                <v-list
                  dense
                  nav
                >
                  <template v-for="(location, index) of appointmentLocations">
                    <v-divider
                      v-if="index > 0"
                      :key="`divider_${location.id}`"
                      class="mb-1"
                    />
                    <v-list-item
                      :key="`item_${location.id}`"
                      @click="_ => selectAppointmentLocation(location.id)"
                    >
                      <v-list-item-content>
                        <v-list-item-title class="font-weight-regular">
                          {{ location.name }}
                        </v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                  </template>
                </v-list>
              </v-menu>
              <v-text-field
                v-model="docketEntry.appointment_location"
                counter="255"
                label="Location *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.appointment_location)"
                @input="_ => v$.docketEntry.appointment_location.$touch()"
                @blur="_ => v$.docketEntry.appointment_location.$touch()"
              />
            </v-col>

            <v-col
              cols="12"
            >
              <v-row>
                <v-col cols="6">
                  <v-menu
                    v-model="visibility.startDatePicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template
                      #activator="{on, attrs}"
                    >
                      <v-text-field
                        :value="displayFormatStartDate"
                        label="First day *"
                        outlined
                        append-icon="mdi-calendar"
                        :error-messages="getErrorMessages(v$.docketEntry.event_datetime_start)"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        @focus="_ => v$.docketEntry.event_datetime_start.$touch()"
                        @blur="_ => v$.docketEntry.event_datetime_start.$touch()"
                      />
                    </template>

                    <v-date-picker
                      v-model="isoFormatStartDate"
                      :min="beginningOfRange"
                      :max="isoFormatEndDate"
                      no-title
                      @input="newDate => onStartDateChanged(newDate)"
                    />
                  </v-menu>
                </v-col>

                <v-col cols="6">
                  <v-menu
                    v-model="visibility.endDatePicker"
                    :close-on-content-click="false"
                    transition="scale-transition"
                    offset-y
                    min-width="auto"
                  >
                    <template
                      #activator="{on, attrs}"
                    >
                      <v-text-field
                        :value="displayFormatEndDate"
                        label="Last day *"
                        outlined
                        append-icon="mdi-calendar"
                        :error-messages="getErrorMessages(v$.docketEntry.event_datetime_end)"
                        readonly
                        v-bind="attrs"
                        v-on="on"
                        @focus="_ => v$.docketEntry.event_datetime_end.$touch()"
                        @blur="_ => v$.docketEntry.event_datetime_end.$touch()"
                      />
                    </template>

                    <v-date-picker
                      v-model="isoFormatEndDate"
                      :min="isoFormatStartDate"
                      no-title
                      @input="newDate => onEndDateChanged(newDate)"
                    />
                  </v-menu>
                </v-col>
              </v-row>
            </v-col>

            <v-col
              cols="12"
            >
              <v-autocomplete
                v-model="docketEntry.timezone"
                :items="timezoneOptions"
                label="Timezone *"
                outlined
                :error-messages="getErrorMessages(v$.docketEntry.timezone)"
                @input="_ => v$.docketEntry.timezone.$touch()"
                @blur="_ => v$.docketEntry.timezone.$touch()"
              />
            </v-col>

            <!-- Availability Validation Error Message -->
            <v-col
              v-if="showAvailabilityError"
              cols="12"
            >
              <v-alert
                type="error"
                outlined
                dense
              >
                <strong>No available time slots found</strong><br>
                The selected date range ({{ displayFormatStartDate }} to {{ displayFormatEndDate }})
                does not have any available appointment slots for the assigned person.
                <br><br>
                <strong>To proceed, you can:</strong>
                <ul class="ma-2">
                  <li>Select a different date range</li>
                  <li>Choose a different assignee</li>
                  <li>Check if availability slots have been created for this appointment type</li>
                </ul>
              </v-alert>
            </v-col>
          </v-row>
        </v-container>
      </v-col>

      <v-col
        cols="6"
        style="min-height: 500px;"
      >
        <DocketEntryEditorAvailability
          ref="availabilityPicker"
          default-calendar-view="month"
          :clickable="false"
          :custom-options="customCalendarOptions"
          :enabled-views="['week', 'month']"
          filter-by-type
          @availability-slots-updated="slots => onAvailabilitySlotsUpdated(slots)"
        />
      </v-col>
    </v-row>

    <ConfirmationDialog
      v-if="isDefaultValuesSelectorDialogVisible"
      v-model="isDefaultValuesSelectorDialogVisible"
      title="Confirmation Request"
      @confirmed="_ => onReplaceWithDocketEntryTypeDefaults()"
    >
      <template #message>
        <p>
          The {{ actionTypeLabel }} <strong>{{ docketEntryType.name }}</strong> contains default values that can replace the existing ones.
        </p>
        <p>Please select what default values should be used:</p>
        <div>
          <div
            v-for="item of replaceableValues"
            :key="item.value"
          >
            <v-checkbox
              v-model="selectedDefaultValues"
              hide-details
              :label="item.label"
              :value="item.value"
            />
          </div>
        </div>
      </template>
    </ConfirmationDialog>
  </v-container>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import moment from 'moment-timezone'
import { required } from '@vuelidate/validators'
import { startCase } from 'lodash'
import { useVuelidate } from '@vuelidate/core'

import { DEFAULT_DATE_DISPLAY_FORMAT, partyName } from '@/utilities'

import { CALENDAR_NAMESPACE } from '@/store/calendar'
import { DOCKET_ENTRY_NAMESPACE } from '@/store/docket-entry'
import { DOCKET_ENTRY_TYPE_NAMESPACE } from '@/store/docket-entry-type'
import { LEGAL_CASE_NAMESPACE } from '@/store/legal-case'
import { SHARED_NAMESPACE } from '@/store/shared'

import ConfirmationDialog from '@/components/Dialogs/Common/ConfirmationDialog.vue'
import DocketEntryEditorAvailability from '@/components/DocketEntry/Editor/DocketEntryEditorAvailability.vue'
import UserSelect from '@/components/Fields/UserSelect.vue'

export default {
  name: 'DocketEntryEditorInviteOverview',

  components: {
    ConfirmationDialog,
    DocketEntryEditorAvailability,
    UserSelect,
  },

  setup () {
    return {
      v$: useVuelidate(),
    }
  },

  data () {
    return {
      appointmentLocations: [],
      attendee: {},
      availabilitySlots: [], // Store availability slots locally for reactivity
      beginningOfRange: null,
      isDefaultValuesSelectorDialogVisible: false,
      replaceableValues: [],
      selectedDefaultValues: [],
      timezoneOptions: [],
      visibility: {
        endDatePicker: false,
        startDatePicker: false,
      },
    }
  },

  validations () {
    return {
      docketEntry: {
        appointment_location: { required },
        assignee_id: { required },
        attendee_id: { required },
        contact_type: { required },
        docket_entry_type_id: { required },
        event_datetime_end: { required },
        event_datetime_start: {
          isAfterNow: value => {
            return moment().utc().isBefore(moment(value))
          },

          required,
        },

        timezone: { required },
      },
    }
  },

  computed: {
    actionTypeLabel () {
      return `${startCase(this.docketEntry.action_type)} Type`
    },

    adHocAllowedUsers () {
      return [
        this.currentUser.id,
        ...this.receivedPermissionsFor(this.currentUser.id, 'CreateAdHocAppointments').map(gp => gp.fromUserId),
      ]
    },

    allowedUsers () {
      return (this.adHocAllowedUsers || []).
        concat(this.bookingAllowedUsers.filter(a => !this.adHocAllowedUsers.includes(a))).
        concat(this.docketEntry.assignee_id)
    },

    attendeeHasEmail () {
      return !!this.partyEmail(this.attendee)
    },

    attendeeHasPhone () {
      return !!this.partyPhone(this.attendee)
    },

    attendees () {
      return this.legalCaseParties.map(party => {
        const email = this.partyEmail(party)
        const phone = this.partyPhone(party)
        let name

        name = partyName(party)
        if (email === undefined) name += ' (no email)'
        if (phone === undefined) name += ' (no phone)'

        return {
          disabled: !(email || phone),
          text: name,
          value: party.id,
        }
      })
    },

    bookingAllowedUsers () {
      return this.receivedPermissionsFor(this.currentUser.id, 'BookAvailabilitySlots').map(gp => gp.fromUserId)
    },

    customCalendarOptions () {
      return {
        selectable: false,
      }
    },

    displayFormatEndDate () {
      if (!this.docketEntry.event_datetime_end) return ''
      const date = moment(this.docketEntry.event_datetime_end)
      return date.isValid() ? date.format(DEFAULT_DATE_DISPLAY_FORMAT) : ''
    },

    displayFormatStartDate () {
      if (!this.docketEntry.event_datetime_start) return ''
      const date = moment(this.docketEntry.event_datetime_start)
      return date.isValid() ? date.format(DEFAULT_DATE_DISPLAY_FORMAT) : ''
    },

    docketEntry () {
      return this.editedDocketEntry
    },

    docketEntryType () {
      return this.editedDocketEntry.docketEntryType
    },

    docketEntryTypeOptions () {
      return this.filteredDocketEntryTypes.
        reduce((res, det) => {
          if (!det.all_legal_case_types && !det.legal_case_type_ids.includes(this.legalCase?.legal_case_type_id)) {
            return res
          }

          res.push({
            has_automated_text: !!det.automated_text_template,
            has_checklist: !!det.checklist,
            has_notifications: !!det.docket_entry_type_notifications.length,
            has_workflows: !!det.workflows.length,
            text: det.name,
            value: det.id,
          })

          return res
        }, [])
    },

    emailLabel () {
      const email = this.partyEmail(this.attendee)

      if (email) {
        return `Email (${email.address})`
      } else {
        return 'Email (not available)'
      }
    },

    filteredDocketEntryTypes () {
      return this.docketEntryTypes.filter(det => {
        return (
          det.action_type === 'Appointment' &&
            det.archived === false
        )
      })
    },

    hasAppointmentLocations () {
      return this.appointmentLocations && this.appointmentLocations.length > 0
    },

    isFormValid () {
      return !this.v$.$invalid && this.hasAvailableSlots()
    },

    isoFormatEndDate: {
      get () {
        if (!this.docketEntry.event_datetime_end) return ''

        const date = moment(this.docketEntry.event_datetime_end)

        return date.isValid() ? date.format('YYYY-MM-DD') : ''
      },

      set () {
        // this is unused, but needs to be here to prevent an error when the date
        // picker tries to set the value
      },
    },

    isoFormatStartDate: {
      get () {
        let date
        if (this.docketEntry.event_datetime_start) {
          date = moment(this.docketEntry.event_datetime_start)
        } else {
          date = moment(this.beginningOfRange)
        }
        return date.isValid() ? date.format('YYYY-MM-DD') : ''
      },

      set () {
        // this is unused, but needs to be here to prevent an error when the date
        // picker tries to set the value
      },
    },

    legalCase () {
      return this.legalCases.find(lc => lc.id === this.docketEntry.legal_case_id)
    },

    legalCaseParties () {
      return this.legalCase?.parties.filter(party => party.is_eco_member) || []
    },

    phoneLabel () {
      const phone = this.partyPhone(this.attendee)

      if (phone) {
        return `Phone (+${phone.country_prefix} ${phone.phone_number})`
      } else {
        return 'Phone (not available)'
      }
    },

    showAvailabilityError () {
      // Only show error if all required fields are filled and form is otherwise valid
      // but we don't have available slots
      const hasRequiredFields = (
        this.docketEntry.assignee_id &&
        this.docketEntry.docket_entry_type_id &&
        this.docketEntry.event_datetime_start &&
        this.docketEntry.event_datetime_end
      )

      return hasRequiredFields && !this.v$.$invalid && !this.hasAvailableSlots()
    },

    ...mapGetters([
      'currentUser',
    ]),

    ...mapGetters(CALENDAR_NAMESPACE, [
      'receivedPermissionsFor',
    ]),

    ...mapGetters(DOCKET_ENTRY_NAMESPACE, [
      'editedDocketEntry',
      'originalDocketEntry',
    ]),

    ...mapGetters(DOCKET_ENTRY_TYPE_NAMESPACE, [
      'docketEntryTypes',
    ]),

    ...mapGetters(LEGAL_CASE_NAMESPACE, [
      'legalCases',
    ]),
  },

  watch: {
    'docketEntry.assignee_id' (val) {
      this.docketEntry.docket_entries_assignments = [{ user_id: val }]
    },

    'docketEntry.attendee_id' (val) {
      this.docketEntry.attendances = [{ party_id: val }]
      this.attendee = this.legalCaseParties.find(party => party.id === val)
    },

    'docketEntry.docket_entries_assignments': {
      deep: true,

      handler (val) {
        this.docketEntry.assignee_id = val[0]?.user_id
      },

      immediate: true,
    },

    'docketEntry.timezone' (val) {
      if (!this.timezoneOptions.find(tzo => tzo.value === val)) {
        this.docketEntry.timezone = this.originalDocketEntry.timezone
      }
    },

    isFormValid: {
      handler (value) {
        this.$emit('update:valid', value)
      },

      immediate: true,
    },
  },

  async created () {
    await this.loadTimezoneOptions()
    this.appointmentLocations = await this.fetchAppointmentLocations()
    this.beginningOfRange = moment().add(1, 'day').format('YYYY-MM-DD')
  },

  mounted () {
    this.$nextTick().then(_ => this.$refs.docketEntryTypeOptions.focus())
  },

  methods: {
    getErrorMessages (validator) {
      return validator?.$errors.map(e => e.$message)
    },

    hasAvailableSlots () {
      const assigneeId = this.docketEntry.assignee_id
      const docketEntryTypeId = this.docketEntry.docket_entry_type_id

      // If no required fields are set, allow submission (other validation will handle)
      if (!assigneeId || !docketEntryTypeId || !this.docketEntry.event_datetime_start || !this.docketEntry.event_datetime_end) {
        return true
      }

      // If no slots available, block submission
      if (!this.availabilitySlots || this.availabilitySlots.length === 0) {
        return false
      }

      // Convert our datetime range to UTC for comparison
      const startDateTime = moment.tz(moment(this.docketEntry.event_datetime_start), this.docketEntry.timezone).utc()
      const endDateTime = moment.tz(moment(this.docketEntry.event_datetime_end), this.docketEntry.timezone).utc()

      if (!startDateTime.isValid() || !endDateTime.isValid()) {
        return true // If dates are invalid, let other validation handle it
      }

      // Check if there are any availability slots within the date range
      const hasMatchingSlot = this.availabilitySlots.some(slot => {
        if (slot.userId !== assigneeId || slot.docketEntryTypeId !== docketEntryTypeId) {
          return false
        }

        const slotStart = moment(slot.startDate).utc()
        const slotEnd = moment(slot.endDate).utc()

        // Check if slot overlaps with our date range (any overlap means availability)
        return slotStart.isBefore(endDateTime) && slotEnd.isAfter(startDateTime)
      })

      return hasMatchingSlot
    },

    async loadTimezoneOptions () {
      this.timezoneOptions = await this.getOptions({ field: 'timezone', model: 'SiteSetting::Timezone' })
    },

    onAvailabilitySlotsUpdated (slots) {
      this.availabilitySlots = slots || []
    },

    onEndDateChanged (newDate) {
      // Store in YYYY-MM-DD format for database compatibility
      this.docketEntry.event_datetime_end = newDate
      this.visibility.endDatePicker = false
    },

    onReplaceWithDocketEntryTypeDefaults () {
      this.isDefaultValuesSelectorDialogVisible = false
    },

    onStartDateChanged (newDate) {
      // Store in YYYY-MM-DD format for database compatibility
      this.docketEntry.event_datetime_start = newDate
      this.visibility.startDatePicker = false
    },

    partyEmail (party) {
      let email

      if (party.emails?.length > 0) {
        email = party.emails.find(email => email.primary)
        email ||= party.emails[0]
      }

      return email
    },

    partyPhone (party) {
      let phone

      if (party.phones?.length > 0) {
        phone = party.phones.find(phone => phone.preferred_text)
      }

      return phone
    },

    selectAppointmentLocation (appointmentLocationId) {
      this.docketEntry.appointment_location = this.appointmentLocations.
        find(al => al.id === appointmentLocationId).
        location
    },

    async validate () {
      await this.v$.$validate()
    },

    ...mapActions(DOCKET_ENTRY_NAMESPACE, [
      'fetchAppointmentLocations',
    ]),

    ...mapActions(SHARED_NAMESPACE, [
      'getOptions',
    ]),
  },
}
</script>
